/**
 * 资源加载器
 * 负责从 IndexedDB 或网络加载静态资源，并进行缓存
 */

import localforage from 'localforage';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';

// 资源存储配置
const RESOURCE_STORAGE_CONFIG = {
  name: 'IsekaiRPG',
  storeName: 'resources',
  description: 'Storage for IsekaiRPG static resources (fonts, images, etc.)',
  version: 1.0,
};

// 初始化资源存储实例
const resourceStorage = localforage.createInstance(RESOURCE_STORAGE_CONFIG);

/**
 * 资源加载器
 * 负责从 IndexedDB 或网络加载静态资源，并进行缓存
 */
export class ResourceLoader {
  /**
   * 加载资源 Blob
   * 优先从 IndexedDB 读取，如果没有则从网络下载并缓存
   * @param url 资源文件 URL
   * @returns 资源的 Blob URL
   */
  static async loadResourceBlob(url: string): Promise<string> {
    try {
      // 使用 URL 作为键名
      const key = url;

      // 1. 尝试从缓存获取
      const cachedBlob = await resourceStorage.getItem<Blob>(key);

      if (cachedBlob) {
        return URL.createObjectURL(cachedBlob);
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch resource: ${response.statusText}`);
      }

      const blob = await response.blob();

      // 3. 存入缓存 (异步，不阻塞返回)
      resourceStorage.setItem(key, blob).catch(err => {
        LogService.warn('ResourceLoader', `缓存资源失败 ${url}`, err);
      });

      return URL.createObjectURL(blob);
    } catch (error) {
      LogService.error('ResourceLoader', `加载资源出错 ${url}`, error as Error);
      // 如果出错，直接返回原始 URL，让浏览器尝试处理
      return url;
    }
  }

  /**
   * 加载图片
   * @param url 图片 URL
   * @returns 图片 Blob URL
   */
  static async loadImage(url: string): Promise<string> {
    return this.loadResourceBlob(url);
  }

  /**
   * 注入字体
   * @param family 字体族名称
   * @param url 字体文件 URL
   * @param options 字体选项 (weight, style 等)
   */
  static async injectFont(family: string, url: string, options: FontFaceDescriptors = {}): Promise<void> {
    try {
      // 获取字体 Blob URL (或原始 URL)
      const fontUrl = await this.loadResourceBlob(url);

      // 创建 FontFace 对象
      const fontFace = new FontFace(family, `url(${fontUrl})`, {
        display: 'block', // 使用 block 策略避免闪烁
        ...options,
      });

      // 加载字体
      await fontFace.load();

      // 添加到文档字体集
      document.fonts.add(fontFace);

      document.fonts.add(fontFace);
    } catch (error) {
      LogService.error('ResourceLoader', `注入字体失败 ${family}`, error as Error);

      // 降级处理：如果 FontFace API 失败，尝试使用传统的 style 注入
      // 但这通常意味着浏览器不支持 FontFace API 或者字体文件损坏
      const style = document.createElement('style');
      style.textContent = `
        @font-face {
          font-family: '${family}';
          src: url('${url}') format('truetype');
          font-weight: ${options.weight || 'normal'};
          font-style: ${options.style || 'normal'};
          font-display: block;
        }
      `;
      document.head.appendChild(style);
    }
  }
}
